import { useEffect } from "react";
import { isRouteErrorResponse, useRevalidator, useRouteError } from "react-router";
import { RotateCcw, TriangleAlert } from "lucide-react";
import { isStaleBuildError, reloadOnceForNewBuild } from "@milqit/ui";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

export function DashboardError() {
  const error = useRouteError();
  const revalidator = useRevalidator();

  // A tab left open across a deploy asks for a page file the new build replaced: load the new
  // build instead of showing an error (once a minute at most, so a real outage can't loop).
  const staleBuild = error instanceof Error && isStaleBuildError(error);
  useEffect(() => {
    if (staleBuild && reloadOnceForNewBuild()) return;
    console.error(error);
  }, [error, staleBuild]);

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : null;

  return (
    <Empty className="mx-auto my-10 max-w-xl border">
      <EmptyHeader>
        <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
          <TriangleAlert />
        </EmptyMedia>
        <EmptyTitle>This page hit an error</EmptyTitle>
        <EmptyDescription>
          Try again — if it keeps happening, share the details below with engineering.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button onClick={() => revalidator.revalidate()} disabled={revalidator.state === "loading"}>
          <RotateCcw /> Try again
        </Button>
        {import.meta.env.DEV && detail ? (
          <p className="text-muted-foreground font-mono text-xs">{detail}</p>
        ) : null}
      </EmptyContent>
    </Empty>
  );
}
